import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, AlertCircle, MapPin, Package, Calendar, CheckCircle2, Clock3, XCircle } from 'lucide-react';
import axios from 'axios';
import { Header } from '@/components/Header';
import { StatusBadge } from '@/components/StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIMELINE = [
  { estado: 'Notificada', icon: CheckCircle2, desc: 'Solicitud recibida y registrada' },
  { estado: 'En espera', icon: Clock3, desc: 'En cola de programación' },
  { estado: 'Completada', icon: Package, desc: 'Enseres recogidos' },
];

function StatusTimeline({ estado }) {
  const statusOrder = { 'Notificada': 0, 'En espera': 1, 'Completada': 2, 'Cancelada': -1 };
  const currentIdx = statusOrder[estado] ?? 0;
  const isCancelled = estado === 'Cancelada';

  return (
    <div className="my-6">
      {isCancelled ? (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <XCircle size={24} className="text-red-500" />
          <div>
            <p className="font-semibold text-red-700">Solicitud cancelada</p>
            <p className="text-sm text-red-500">Esta solicitud ha sido cancelada.</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center">
          {TIMELINE.map((item, idx) => {
            const Icon = item.icon;
            const isDone = idx <= currentIdx;
            const isActive = idx === currentIdx;
            return (
              <React.Fragment key={item.estado}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    isActive ? 'bg-[#009940] border-[#009940] text-white' :
                    isDone ? 'bg-[#374B67] border-[#374B67] text-white' :
                    'bg-white border-slate-200 text-slate-400'
                  }`}>
                    <Icon size={18} />
                  </div>
                  <p className={`text-xs font-medium mt-2 text-center ${
                    isActive ? 'text-[#009940]' : isDone ? 'text-[#374B67]' : 'text-slate-400'
                  }`}>{item.estado}</p>
                  <p className="text-xs text-slate-400 text-center hidden sm:block">{item.desc}</p>
                </div>
                {idx < TIMELINE.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 mb-5 ${ idx < currentIdx ? 'bg-[#374B67]' : 'bg-slate-200' }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ConsultarEstado() {
  const [searchParams] = useSearchParams();
  const [ticket, setTicket] = useState(searchParams.get('ticket') || '');
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef();

  useEffect(() => {
    if (searchParams.get('ticket')) {
      handleSearch(searchParams.get('ticket'));
    }
  }, []);

  const handleSearch = async (code) => {
    const q = (code || ticket).trim().toUpperCase();
    if (!q) { setError('Introduce tu código de ticket.'); return; }
    setLoading(true);
    setError('');
    setSolicitud(null);
    try {
      const resp = await axios.get(`${API}/solicitudes/ticket/${q}`);
      setSolicitud(resp.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No se encontró ninguna solicitud con ese código.');
      } else {
        setError('Error al consultar. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <Header />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="text-2xl font-semibold text-[#374B67] mb-1">Consultar estado de solicitud</h1>
        <p className="text-slate-500 text-sm mb-8">Introduce tu código de ticket para ver el estado de tu solicitud de recogida.</p>

        {/* Search */}
        <div className="flex gap-2 mb-6">
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={ticket}
              onChange={e => setTicket(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="MAJ-20250101-0001"
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#69C7EA] focus:border-transparent"
              data-testid="ticket-lookup-input"
            />
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={loading}
            className="flex items-center gap-2 bg-[#009940] hover:bg-[#007F35] text-white px-5 py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60"
            data-testid="ticket-lookup-submit-button"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? '' : 'Buscar'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <AlertCircle size={18} className="text-red-500" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Result */}
        {solicitud && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Código</p>
                <p className="ticket-code text-xl text-[#374B67] mt-0.5">{solicitud.ticket_code}</p>
              </div>
              <StatusBadge status={solicitud.estado} size="lg" />
            </div>

            <StatusTimeline estado={solicitud.estado} />

            {/* Details */}
            <div className="space-y-2 mt-4">
              {[
                { icon: MapPin, label: 'Zona', value: solicitud.zona },
                { icon: Package, label: 'Tipo de enseres', value: solicitud.tipo_enseres },
                { icon: Calendar, label: 'Fecha preferida', value: solicitud.fecha_preferida },
                { icon: Calendar, label: 'Fecha de solicitud', value: `${solicitud.fecha_peticion} ${solicitud.hora_peticion}` },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-center gap-3 py-2.5 border-b border-slate-100">
                  <Icon size={15} className="text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-500 w-32">{label}</span>
                  <span className="text-sm font-medium text-slate-900">{value}</span>
                </div>
              ))}
            </div>

            {solicitud.imagen_url && (
              <div className="mt-4">
                <p className="text-xs text-slate-500 mb-2">Imagen adjunta</p>
                <img
                  src={`${BACKEND_URL}${solicitud.imagen_url}`}
                  alt="Enseres"
                  className="w-full max-w-xs h-40 object-cover rounded-xl border border-slate-200"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
